import { v4 as uuid } from 'uuid';
import { toUUID } from '../../util/transform';
import type { IAddress } from './Address.interface';

export default class Address implements IAddress {
  id: string;
  idLinkedAddress: string;
  placeId: string;
  suiteNumber: string;
  streetNumber: string;
  street: string;
  streetType: string;
  suburb: string;
  city: string;
  state: string;
  countryCode: string;
  country: string;
  latitude: number;
  longitude: number;
  postCode: string;
  district: string;
  formatted: string;
  postOfficeBox: string;
  isPostOfficeBoxEnabled: boolean;

  static hasAddress = (address) => {
    if (address?.country === 'Australia' || address?.countryCode === 'AU') {
      return address?.isPostOfficeBoxEnabled
        ? address?.postOfficeBox &&
            (address?.city || address?.suburb) &&
            address?.state &&
            address?.postCode
        : address?.streetNumber &&
            address?.street &&
            (address?.city || address?.suburb) &&
            address?.state &&
            address?.postCode;
    }
    return address?.isPostOfficeBoxEnabled
      ? address?.postOfficeBox && address?.city && address?.postCode
      : address?.streetNumber && address?.street && address?.city && address?.postCode;
  };

  static copy = (addressToCopy) =>
    Address.build({
      ...addressToCopy,
      idLinkedAddress: addressToCopy.idLinkedAddress || addressToCopy.id,
      id: ''
    });

  static getAddress = (address) => {
    const formattedAddress = [];
    if (address) {
      const firstPart = [];
      if (!address?.isPostOfficeBoxEnabled) {
        if (address?.streetNumber) {
          const fullNumber = address?.suiteNumber
            ? `${address?.suiteNumber}/${address?.streetNumber}`
            : address?.streetNumber;
          firstPart.push(fullNumber);
        }
        if (address?.street) {
          firstPart.push(address?.street);
        }
        if (address?.streetType) {
          firstPart.push(address?.streetType);
        }
        if (firstPart.length) {
          const partOne = `${firstPart.join(' ').toString().trim()},`;
          formattedAddress.push(partOne);
        }
      } else {
        formattedAddress.push(address?.postOfficeBox);
      }
      const secondPart = [];
      if (address?.suburb) {
        secondPart.push(address?.suburb);
      }
      if (address?.city && address?.country !== 'Australia') {
        secondPart.push(address?.city);
      }
      if (address?.state) {
        if (address?.country === 'Australia') {
          secondPart.push(String(address?.state).toUpperCase());
        } else {
          secondPart.push(address?.state);
        }
      }
      if (address?.postCode) {
        secondPart.push(address?.postCode);
      }
      if (secondPart.length) {
        const partTwo = `${secondPart.join(' ').toString().trim()},`;
        formattedAddress.push(partTwo);
      }
      if (address?.country) {
        formattedAddress.push(address?.country);
      }
    }
    return formattedAddress.length ? formattedAddress.join(' ') : 'No address';
  };

  static build = (data: IAddress) => new Address(data);
  static blank = () => new Address({ id: uuid() });

  /**
   * Constructor
   * @param {IAddress} data
   */
  constructor(data?: IAddress) {
    this.id = toUUID(data?.id) || uuid();
    this.idLinkedAddress = toUUID(data?.idLinkedAddress);
    this.placeId = data?.placeId || '';
    this.suiteNumber = data?.suiteNumber || '';
    this.streetNumber = data?.streetNumber || '';
    this.street = data?.street || '';
    this.streetType = data?.streetType || '';
    this.suburb = data?.suburb || data?.city || '';
    this.city = data?.city || '';
    this.state = data?.state || '';
    this.countryCode = data?.countryCode || '';
    this.country = data?.country || undefined;
    this.latitude = parseFloat(data?.latitude || 0);
    this.longitude = parseFloat(data?.longitude || 0);
    this.postCode = data?.postCode || '';
    this.district = data?.district || '';
    this.formatted = data?.formatted || '';
    this.postOfficeBox = data?.postOfficeBox || '';
    this.isPostOfficeBoxEnabled = data?.isPostOfficeBoxEnabled === true;
  }
}
